export const formatLastModified = (dateString) => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffHours / 24;
  
  // If less than 1 hour ago, show minutes
  if (diffHours < 1) {
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes === 1) return '1 minute ago';
    return `${diffMinutes} minutes ago`;
  }
  
  // If less than 24 hours ago, show time
  if (diffHours < 24) {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
  }
  
  // If less than 7 days ago, show day and time
  if (diffDays < 7) {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
    return `${dayName} ${time}`;
  }
  
  // Otherwise show date
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: diffDays > 365 ? 'numeric' : undefined
  });
};

export const formatDate = (dateString, format) => {
  if (!dateString) return '';

  const date = new Date(dateString);

  // If format is 'input', return yyyy-MM-dd for HTML date inputs
  // Use UTC methods to avoid timezone conversion issues
  if (format === 'input') {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Default format as MM/DD/YY for display
  // Use UTC methods to avoid timezone conversion issues
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatProjectedDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  // Format as MM/DD/YY - use UTC methods to avoid timezone conversion
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

/**
 * Calculate business days between two dates (excludes weekends)
 * Positive result = dueDate is after projectedDate (ahead of schedule)
 * Negative result = dueDate is before projectedDate (overdue)
 * @param {string|Date} projectedDateString - The projected completion date
 * @param {string|Date} dueDateString - The due date
 * @returns {number} Number of business days (can be negative)
 */
export const calculateBusinessDays = (projectedDateString, dueDateString) => {
  if (!projectedDateString || !dueDateString) return null;

  const projectedDate = new Date(projectedDateString);
  const dueDate = new Date(dueDateString);

  // Ensure dates are valid
  if (isNaN(projectedDate.getTime()) || isNaN(dueDate.getTime())) return null;

  let count = 0;
  const increment = projectedDate < dueDate ? 1 : -1;
  const currentDate = new Date(projectedDate);

  // Iterate through each day between the two dates
  while (
    (increment === 1 && currentDate < dueDate) ||
    (increment === -1 && currentDate > dueDate)
  ) {
    const dayOfWeek = currentDate.getDay();
    // Count only weekdays (Monday = 1, Friday = 5)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count += increment;
    }
    currentDate.setDate(currentDate.getDate() + increment);
  }

  return count;
};